import { useState } from 'react'

import type { ValueLabelPair } from '@skytech/ui'
import { Select, Input, MultiSelect, MessagePopup, LoaderCircle } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { ExternalLink } from '@skytech/ui/ExternalLink'
import { getLocaleTimezoneIsoDate, getMaxDate, getMinDate } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import CancelIcon from 'mdi-react/CancelIcon'
import GlobeIcon from 'mdi-react/GlobeIcon'
import PinIcon from 'mdi-react/PinIcon'
import { useNavigate, useParams } from 'react-router-dom'
import type { MultiValue } from 'react-select'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import DataPoints, { DataPoint } from '@/components/DataPoints'
import { LoadingError } from '@/components/LoadingError'
import { LocalDate } from '@/components/LocalDate'
import { useCanManageOnOffboarding } from '@/hooks/access'
import { useProfile } from '@/hooks/user'
import subscriptionResources from '@/modules/subscriptions/subscription/resources'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { commonSubscriptionResources } from '@/resources/subscriptions'
import { FORM_URL } from '@/util/misc'

import { SUB_SERVICE_ORDER_STATUS } from '../constants'

import { CancelOrderModal } from './CancelOrderModal'
import { getIsActivationDateValid } from './helpers'
import queries from './queries'
import type { EditSubscriptionPayload } from './types'

import './SubscriptionPage.styles.scss'

function getEcomPolicyIdList(policyIds: string) {
  const idList = policyIds.replace('[', '').replace(']', '')
  if (!idList) return []
  return idList.split(',').map(Number)
}

export function getStatusBadge(statusId: number) {
  if ([SUB_SERVICE_ORDER_STATUS.NEW, SUB_SERVICE_ORDER_STATUS.COMPLETED].includes(statusId))
    return 'positive'
  if ([SUB_SERVICE_ORDER_STATUS.TERMINATED, SUB_SERVICE_ORDER_STATUS.CANCELLED].includes(statusId))
    return 'negative'

  return 'neutral'
}

type FormFields = {
  name: string
  activationDate: string
  contactNumber: string
  email: string
  departmentId: number | string
  salaryDeductionId: number | string
  ecomPolicyIdList: number[]
  devicePolicyId: number | string
}

export const SubscriptionPage = () => {
  const profile = useProfile()
  const translate = useTranslation()
  const canManageOnOffboarding = useCanManageOnOffboarding()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isResendFormButtonDisabled, setIsResendFormButtonDisabled] = useState(false)
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const params = useParams()
  const subscriptionId = Number(params.subscriptionId)

  if (isNaN(subscriptionId)) throw Error(translate('Subscription ID is not a number!'))

  const {
    data: subscription,
    isError,
    refetch: updateSubscription,
  } = useQuery(queries.subscription(subscriptionId), () =>
    subscriptionResources.get(subscriptionId)
  )
  const { data: ecomPolicies, isLoading: isEcomPoliciesLoading } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const { data: setup, isLoading: isSetupLoading } = useQuery(
    commonQueries.subscriptionSetup(),
    commonSubscriptionResources.getSetup
  )

  const editSubMutation = useMutation(
    (payload: EditSubscriptionPayload) =>
      subscriptionResources.editSubscription({ subscriptionId: Number(subscriptionId), payload }),
    {
      onSuccess() {
        updateSubscription()

        queryClient.invalidateQueries(queries.subscription(Number(subscriptionId)))
        queryClient.invalidateQueries(queries.subscriptionList(), { refetchType: 'all' })
      },
    }
  )
  const resendFormMutation = useMutation(subscriptionResources.resendForm)

  const handleResendForm = () => {
    resendFormMutation.mutate(Number(subscriptionId), {
      onSuccess() {
        setIsResendFormButtonDisabled(true)
        setIsSuccessPopupOpen(true)

        setTimeout(() => {
          setIsResendFormButtonDisabled(false)
        }, 5 * 1000)
      },
    })
  }

  if (isError) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <LoadingError />
      </Container>
    )
  }

  if (!subscription) {
    return (
      <Container onSwipedLeft={() => navigate(-1)}>
        <LoaderCircle />
      </Container>
    )
  }

  const ecomPoliciesOptions = ecomPolicies
    ?.filter(policy => policy.customerHeadName || policy.customerId === subscription?.customerId)
    .map(policy => ({
      value: policy.id,
      label: (
        <span>
          {policy.isGlobal ? (
            <GlobeIcon className="mb-0.5 mr-1 h-4 w-4" />
          ) : (
            <PinIcon className="mb-0.5 mr-1 h-4 w-4" />
          )}
          {policy.name}
        </span>
      ),
    }))

  const departmentOptions = setup?.departments
    ?.filter(
      department => department.customerHeadId || department.customerId === subscription?.customerId
    )
    .map(department => ({
      value: department.id,
      label: department.name,
    }))

  const isEditable = subscription.status === 0 && canManageOnOffboarding
  const ecomPolicyIdList = getEcomPolicyIdList(subscription.ecomPolicyIdList || '')
  const subscriptionStatus = Object.entries(SUB_SERVICE_ORDER_STATUS).find(
    ([, val]) => val === subscription.status
  )?.[0]

  return (
    <Container onSwipedLeft={() => navigate(-1)}>
      <div className="subscription">
        <div className="header">
          <Container.Header>
            <Translation id="Subscription" /> {subscription.id}
          </Container.Header>

          <div className="status">
            <Badge type={getStatusBadge(subscription.status)}>
              <Translation id={`sub_service_order_status/${subscriptionStatus}`} />
            </Badge>
          </div>

          {subscription.comment && (
            <div className="mb-4 whitespace-pre-line rounded bg-amber-100 p-4">
              {subscription.comment}
            </div>
          )}
        </div>

        <section className="sub-data-points">
          <DataPoints>
            <DataPoint
              header={<Translation id="Form" />}
              value={
                <ExternalLink
                  withIcon
                  isWhiteLabeled
                  size="semi-large"
                  href={FORM_URL.replace(':code', subscription.code)}
                >
                  <Translation id="View" />
                </ExternalLink>
              }
            />

            <DataPoint
              header={<Translation id="Created" />}
              value={<LocalDate date={subscription.created} />}
            />

            <DataPoint
              header={<Translation id="Created by" />}
              // createdByName ? createdByName : translate('Automated process')
              value={
                subscription.createdByName
                  ? subscription.createdByName
                  : translate('Automated process')
              }
            />
          </DataPoints>

          <DataPoints>
            <div>
              {subscription.apiSupplierOrderStatus && (
                <DataPoint
                  header={<Translation id="Carrier status" />}
                  value={subscription.apiSupplierOrderStatus}
                />
              )}

              {subscription.apiSupplierOrderStatusReason && (
                <DataPoint
                  header={<Translation id="Reason" />}
                  value={subscription.apiSupplierOrderStatusReason}
                />
              )}

              <DataPoint
                header={<Translation id="Dealer comment" />}
                value={subscription.dealerComment || '-'}
              />

              {subscription.userComment && (
                <DataPoint
                  header={<Translation id="User comment" />}
                  value={subscription.userComment || '-'}
                />
              )}
            </div>
          </DataPoints>
        </section>

        <Formik
          validateOnBlur={false}
          initialValues={{
            name: subscription.name,
            activationDate: getLocaleTimezoneIsoDate(subscription.activationDate),
            contactNumber: subscription.contactNumber || '',
            email: subscription.email,
            departmentId: subscription.departmentId || '',
            salaryDeductionId: subscription.salaryDeductionProfileId || '',
            devicePolicyId: subscription.devicePolicyId || '',
            ecomPolicyIdList,
          }}
          validate={values => {
            const errors: Partial<Record<keyof FormFields, string>> = {}

            if (!values.activationDate) {
              errors.activationDate = translate('This field is mandatory')
            }

            if (values.activationDate && !getIsActivationDateValid(values.activationDate)) {
              errors.activationDate = translate(
                'The provided date must fall between 3 months prior to today and 1 year after today'
              )
            }

            if (!values.email) {
              errors.email = translate('This field is mandatory')
            }

            if (!values.name) {
              errors.name = translate('This field is mandatory')
            }

            if (!values.contactNumber) {
              errors.contactNumber = translate('This field is mandatory')
            }

            if (!values.departmentId) {
              errors.departmentId = translate('This field is mandatory')
            }
            return errors
          }}
          onSubmit={formData => {
            const data: EditSubscriptionPayload = {
              name: formData.name,
              email: formData.email,
              activationDate: formData.activationDate.concat('T00:00:00-0000'),
              departmentId: Number(formData.departmentId),
              ecomPolicyIdList: formData.ecomPolicyIdList,
              contactNumber: formData.contactNumber,
            }
            if (formData.salaryDeductionId)
              data.salaryDeductionId = Number(formData.salaryDeductionId)
            if (formData.devicePolicyId) data.devicePolicyId = Number(formData.devicePolicyId)

            editSubMutation.mutate(data)
          }}
        >
          {({ values }) => {
            const telePolicyOptions = setup?.telePolicies
              ?.filter(
                policy =>
                  (policy.customerHeadId || policy.customerId === subscription?.customerId) &&
                  policy.departmentId === values.departmentId
              )
              .map(policy => ({
                value: policy.id,
                label: (
                  <span>
                    {policy.isGlobal ? (
                      <GlobeIcon className="mb-0.5 mr-1 h-4 w-4" />
                    ) : (
                      <PinIcon className="mb-0.5 mr-1 h-4 w-4" />
                    )}
                    {policy.name}
                  </span>
                ),
              }))
            const devicePolicyOptions = profile?.hasDevicePolicy
              ? setup?.devicePolicies
                  ?.filter(
                    policy =>
                      (policy.customerHeadId || policy.customerId === subscription?.customerId) &&
                      policy.departmentId === values.departmentId
                  )
                  .map(policy => ({
                    value: policy.id,
                    label: (
                      <span>
                        {policy.isGlobal ? (
                          <GlobeIcon className="mb-0.5 mr-1 h-4 w-4" />
                        ) : (
                          <PinIcon className="mb-0.5 mr-1 h-4 w-4" />
                        )}
                        {policy.name}
                      </span>
                    ),
                  }))
              : []

            return (
              <Form>
                <div className="input-section">
                  <div>
                    <Field name="name">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          id="name"
                          marginBottom={16}
                          label={<Translation id="Name" />}
                          error={meta.error}
                          touched={meta.touched}
                          isDisabled={!isEditable}
                          {...field}
                        />
                      )}
                    </Field>

                    <Field name="email">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          id="email"
                          marginBottom={16}
                          label={<Translation id="Email" />}
                          error={meta.error}
                          touched={meta.touched}
                          isDisabled={!isEditable}
                          {...field}
                        />
                      )}
                    </Field>

                    <Field name="contactNumber">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          id="contactNumber"
                          marginBottom={16}
                          label={<Translation id="Contact number" />}
                          error={meta.error}
                          touched={meta.touched}
                          isDisabled={!isEditable}
                          {...field}
                        />
                      )}
                    </Field>

                    <Field name="activationDate">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          id="activationDate"
                          type="date"
                          marginBottom={16}
                          label={<Translation id="Activation date" />}
                          min={getLocaleTimezoneIsoDate(getMinDate(3))}
                          max={getLocaleTimezoneIsoDate(getMaxDate(12))}
                          error={meta.error}
                          touched={meta.touched}
                          isDisabled={!isEditable}
                          {...field}
                        />
                      )}
                    </Field>
                  </div>

                  <div>
                    <Field name="departmentId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          id="departmentId"
                          label={<Translation id="Department" />}
                          options={departmentOptions || []}
                          marginBottom={16}
                          {...field}
                          isLoading={isSetupLoading}
                          isDisabled={!isEditable}
                          onChange={option => form.setFieldValue(field.name, option?.value)}
                        />
                      )}
                    </Field>

                    <Field name="salaryDeductionId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          id="salaryDeductionId"
                          label={<Translation id="Tele policy" />}
                          options={telePolicyOptions || []}
                          marginBottom={16}
                          {...field}
                          isLoading={isSetupLoading}
                          isDisabled={!isEditable}
                          onChange={option => form.setFieldValue(field.name, option?.value)}
                        />
                      )}
                    </Field>

                    {profile?.hasDevicePolicy && (
                      <Field name="devicePolicyId">
                        {({ field, form }: FieldProps) => (
                          <Select
                            id="devicePolicyId"
                            label={<Translation id="Device policy" />}
                            options={devicePolicyOptions || []}
                            marginBottom={16}
                            {...field}
                            isLoading={isSetupLoading}
                            isDisabled={!isEditable}
                            onChange={option => form.setFieldValue(field.name, option?.value)}
                          />
                        )}
                      </Field>
                    )}

                    <Field name="ecomPolicyIdList">
                      {({ field, form }: FieldProps) => (
                        <MultiSelect
                          id="ecomPolicyIdList"
                          label={<Translation id="Ecom policies" />}
                          options={ecomPoliciesOptions || []}
                          marginBottom={16}
                          {...field}
                          isLoading={isEcomPoliciesLoading}
                          isDisabled={!isEditable}
                          onChange={(options: MultiValue<ValueLabelPair> | null) => {
                            form.setFieldValue(
                              field.name,
                              options?.map(option => option.value)
                            )
                          }}
                        />
                      )}
                    </Field>
                  </div>
                </div>

                <section className="submit-section">
                  <Button variant="tertiary" onClick={() => navigate(-1)}>
                    <Translation id="Back to list" />
                  </Button>

                  {canManageOnOffboarding && subscription.status === 0 && (
                    <>
                      <Button
                        variant="danger"
                        Icon={<CancelIcon />}
                        onClick={() => setIsCancelModalOpen(true)}
                        disabled={subscription.status !== 0}
                      >
                        <Translation id="Cancel Order" />
                      </Button>

                      <Button
                        disabled={isResendFormButtonDisabled}
                        isLoading={resendFormMutation.isLoading}
                        onClick={handleResendForm}
                      >
                        <Translation id="Resend form to user" />
                      </Button>

                      <Button variant="primary" type="submit" isLoading={editSubMutation.isLoading}>
                        <Translation id="Save" />
                      </Button>
                    </>
                  )}
                </section>
              </Form>
            )
          }}
        </Formik>
      </div>
      <CancelOrderModal
        id={subscription.id}
        isOpen={isCancelModalOpen && subscription.status === 0}
        onClose={() => setIsCancelModalOpen(false)}
      />

      <MessagePopup
        type="success"
        message={<Translation id="The form has been successfully sent to the user." />}
        title={<Translation id="Subscription form resent" />}
        isOpen={isSuccessPopupOpen}
        onClose={() => setIsSuccessPopupOpen(false)}
      />
    </Container>
  )
}
